import { useEffect, useMemo, useRef, useState } from 'react';
import { applicantEmail, type PublicForm } from '@jfh/intake-shared';
import { Icon } from './Icon.js';
import logoLockupColour from './assets/logo-lockup-colour-trim.png';
import { Review } from './Review.js';
import { ErrorSummary, StepHeader } from './StepHeader.js';
import { loadForm, lookupCompany, lookupPostcode, submitForm, type FormLoad } from './lib/api.js';
import { PostcodeLookupContext } from './lib/postcodeLookup.js';
import { buildSubmitData, sampleValues } from './lib/steps.js';
import { StepFields } from './StepFields.js';
import { useApplicantForm } from './useApplicantForm.js';
import {
  clearDraft,
  draftHasContent,
  loadDraft,
  saveDraft,
} from './lib/draft.js';

/** Read the form slug from `/f/:slug`, falling back to `?slug=`. */
function readSlug(): string | null {
  const path = window.location.pathname.replace(/^\/+/, '');
  const m = /^f\/([^/]+)/.exec(path);
  if (m && m[1]) return decodeURIComponent(m[1]);
  const q = new URLSearchParams(window.location.search).get('slug');
  return q && q.trim() ? q.trim() : null;
}

/** Post the current scroll height to the host (Wix) so the iframe can resize. */
function useAutoResize(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const post = () => {
      const height = Math.ceil(
        Math.max(el.scrollHeight, document.documentElement.scrollHeight),
      );
      window.parent.postMessage({ type: 'jfh-form-resize', height }, '*');
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(el);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [ref]);
}

export function App() {
  const rootRef = useRef<HTMLDivElement>(null);
  useAutoResize(rootRef);

  const slug = useMemo(readSlug, []);
  const [load, setLoad] = useState<FormLoad | { kind: 'loading' }>({
    kind: 'loading',
  });

  useEffect(() => {
    if (!slug) return;
    let live = true;
    setLoad({ kind: 'loading' });
    loadForm(slug).then((r) => {
      if (live) setLoad(r);
    });
    return () => {
      live = false;
    };
  }, [slug]);

  return (
    <div ref={rootRef} className="rdr-root">
      <PageChrome>
        {!slug ? (
          <Notice
            title="No form specified"
            body="Open this page with a form address like /f/your-form-slug."
          />
        ) : load.kind === 'loading' ? (
          <Notice title="Loading…" body="Fetching the form." />
        ) : load.kind === 'not_found' ? (
          <Notice
            title="Form not available"
            body="This form isn’t published, or the address is wrong. Please check the link."
          />
        ) : load.kind === 'error' ? (
          <Notice title="Something went wrong" body={load.message} />
        ) : (
          <PostcodeLookupContext.Provider value={lookupPostcode}>
            <FormFlow form={load.form} slug={slug} />
          </PostcodeLookupContext.Provider>
        )}
      </PageChrome>
    </div>
  );
}

/** Faux Wix page chrome wrapping the embedded form. */
function PageChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="rdr-page">
      <div className="rdr-brand">
        <div className="rdr-wordmark">
          <img src={logoLockupColour} alt="JFH Horticultural Supplies Ltd" />
        </div>
        <div className="rdr-tagline">Trade supplies for growers for over sixty years</div>
      </div>
      <div className="card rdr-form-card">{children}</div>
    </div>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="rdr-notice fade-in">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'done'; email: string | null }
  | { kind: 'validation'; details: string[] }
  | { kind: 'error'; message: string };

function FormFlow({ form, slug }: { form: PublicForm; slug: string }) {
  // Test mode (admin "Test form" → /f/<slug>?test=1): prefill sample data and
  // skip the save-for-later draft so it doesn't clobber a real applicant's draft.
  const testMode = useMemo(
    () => new URLSearchParams(window.location.search).has('test'),
    [],
  );

  const flow = useApplicantForm(form, {
    lookupCompany,
    initialValues: testMode ? () => sampleValues(form.fields) : undefined,
  });
  const { pages, values, step, isReview, reviewIndex } = flow;
  const total = reviewIndex + 1;

  const [turnstile, setTurnstile] = useState(false);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });

  // Save-for-later: offer to resume a saved draft, and auto-save progress.
  const savedDraft = useMemo(() => loadDraft(slug), [slug]);
  const [showResume, setShowResume] = useState(
    () => !testMode && draftHasContent(savedDraft),
  );
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    // Don't overwrite a saved draft until the applicant decides resume/start over.
    // Test runs never touch the draft store.
    if (testMode || showResume || submit.kind === 'done') return;
    saveDraft(slug, { values, step, savedAt: new Date().toISOString() });
  }, [values, step, showResume, submit.kind, slug, testMode]);

  // "Saved on this device" is for the step it was saved on.
  useEffect(() => setSavedAt(null), [step]);

  const resumeDraft = () => {
    if (savedDraft) {
      flow.setValues(savedDraft.values);
      flow.setStep(Math.min(savedDraft.step, total - 1));
    }
    setShowResume(false);
  };
  const discardDraft = () => {
    clearDraft(slug);
    setShowResume(false);
  };
  const saveForLater = () => {
    saveDraft(slug, { values, step, savedAt: new Date().toISOString() });
    setSavedAt(new Date().toISOString());
  };

  const canSubmit =
    submit.kind !== 'sending' && (!form.turnstile || turnstile);

  // Enter in a single-line field submits the step's form: advance, or send on
  // the review step. Multiline (textarea) inputs keep their normal newline.
  const onStepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReview) {
      if (canSubmit) void doSubmit();
    } else {
      flow.goNext();
    }
  };

  const doSubmit = async () => {
    // Final guard: re-validate every page before sending.
    if (!flow.checkAll()) return;

    setSubmit({ kind: 'sending' });
    // Send exactly what's on screen (rules that read the country's iQ details included).
    const data = buildSubmitData(form.fields, values, flow.vis);
    const outcome = await submitForm(slug, {
      data,
      turnstileToken: form.turnstile ? 'placeholder-token' : undefined,
    });
    if (outcome.kind === 'ok') {
      clearDraft(slug);
      setSubmit({ kind: 'done', email: applicantEmail(form.fields, values) || null });
    }
    else if (outcome.kind === 'validation')
      setSubmit({ kind: 'validation', details: outcome.details });
    else setSubmit({ kind: 'error', message: outcome.message });
  };

  // A fresh, empty application (test runs get fresh sample answers).
  const reset = () => {
    flow.reset(testMode ? sampleValues(form.fields) : undefined);
    setTurnstile(false);
    setSavedAt(null);
    setSubmit({ kind: 'idle' });
  };

  if (showResume) {
    return (
      <div className="rdr-resume fade-in">
        <div className="rdr-resume-mark">
          <Icon name="clock" size={24} />
        </div>
        <h3>Welcome back</h3>
        <p>
          You saved this application on this device
          {savedDraft?.savedAt
            ? ` from ${new Date(savedDraft.savedAt).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}`
            : ''}
          . Carry on where you left off?
        </p>
        <div className="rdr-resume-actions">
          <button type="button" className="btn primary" onClick={resumeDraft}>
            Carry on
          </button>
          <button type="button" className="btn" onClick={discardDraft}>
            Start over
          </button>
        </div>
      </div>
    );
  }

  if (submit.kind === 'done') {
    return (
      <div className="rdr-success fade-up">
        <div className="rdr-success-mark">
          <Icon name="check" size={26} />
        </div>
        <h3>Application received</h3>
        <p>
          {form.success ??
            'Thanks, we’ve received your application and will be in touch shortly.'}
        </p>
        {submit.email && <p>We’ll email you at {submit.email}.</p>}
        <button type="button" className="btn" onClick={reset}>
          Start another application
        </button>
      </div>
    );
  }

  const page = pages[step];

  return (
    <form key={step} className="fade-in" onSubmit={onStepSubmit} noValidate>

      {testMode && (
        <div
          style={{
            margin: '0 0 14px',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 600,
            background: 'var(--accent-soft)',
            color: 'var(--accent-text)',
          }}
        >
          Test run: sample answers are filled in. Sending it still creates a real application.
        </div>
      )}

      <StepHeader flow={flow} />
      <ErrorSummary flow={flow} />

      {isReview ? (
        <Review
          pages={pages}
          values={flow.visValues}
          visible={flow.vis}
          onEdit={(i) => {
            setSubmit({ kind: 'idle' });
            flow.setStep(i);
          }}
          turnstileEnabled={form.turnstile}
          turnstileChecked={turnstile}
          onToggleTurnstile={() => setTurnstile((t) => !t)}
        />
      ) : (
        <StepFields flow={flow} fields={page?.fields ?? []} />
      )}

      {submit.kind === 'validation' && (
        <div className="rdr-submit-error">
          <Icon name="alert" size={16} />
          <div>
            <strong>
              {submit.details.length === 1
                ? 'There’s 1 thing to fix'
                : `There are ${submit.details.length} things to fix`}
            </strong>
            <ul>
              {submit.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {submit.kind === 'error' && (
        <div className="rdr-submit-error">
          <Icon name="alert" size={16} />
          <span>{submit.message}</span>
        </div>
      )}

      <div className="rdr-nav">
        {(isReview ? flow.activeCount > 0 : flow.hasPrevSection) && (
          <button type="button" className="btn lg" onClick={flow.goBack}>
            <Icon name="arrowLeft" size={16} /> Back
          </button>
        )}
        {isReview ? (
          <button
            type="submit"
            className="btn primary lg rdr-grow"
            disabled={!canSubmit}
          >
            <Icon name="send" size={16} />
            {submit.kind === 'sending' ? 'Sending…' : 'Send application'}
          </button>
        ) : (
          <button type="submit" className="btn primary lg rdr-grow">
            Continue <Icon name="chevronR" size={16} />
          </button>
        )}
      </div>

      {!testMode && (
        <div className="rdr-later">
          {savedAt ? (
            <span className="saved" role="status">
              <Icon name="check" size={14} /> Saved on this device. You can close this page and come back to finish.
            </span>
          ) : (
            <button type="button" onClick={saveForLater}>
              Save and finish later
            </button>
          )}
        </div>
      )}
    </form>
  );
}
